<template>
  <div class="card-resumen">
    <div class="card-body">
      <div class="title text-primary pb-3">{{ $t('reserva.howPay') }}</div>
      <div>
        <div v-if="!isPagoExternoWithHitos" class="d-flex align-items-center justify-content-between mb-2">
          <input
            id="precioTotal"
            v-model="isPagoUnicoSelected"
            type="radio"
            name="precioTotal"
            :value="true"
            class="d-none"
            @change="optionsChange(true)" />
          <label
            for="precioTotal"
            class="w-100 d-flex align-items-center justify-content-between pointer"
            :class="{ 'fw-bold': isPagoUnicoSelected }">
            <span v-if="!isPagosSucesivos" class="me-4 text-primary">
              {{ $t('reserva.pagarTotalReserva') }} {{ precioTotal }} {{ agenciaStore.getCurrency }}
            </span>
            <span v-else class="me-4 text-primary">
              {{ $t('reserva.pagarTotalRestante') }} ({{ totalPagarRestante }} {{ agenciaStore.getCurrency }})
            </span>
            <span class="d-flex justify-content-center align-items-center me-4">
              <RadioButtonIcon :is-selected="isPagoUnicoSelected" />
            </span>
          </label>
        </div>
        <template v-if="hitosPago.length > 1 && !isPagoUnicoForced">
          <div v-if="!isPagoExternoWithHitos">
            <input
              id="precioInicial"
              v-model="isPagoUnicoSelected"
              type="radio"
              name="precioInicial"
              :value="false"
              class="d-none"
              :disabled="fechaVencimientoHitoActualAlreadyPayed !== null"
              :class="{ 'opacity-50': fechaVencimientoHitoActualAlreadyPayed !== null }"
              @change="optionsChange(false)" />
            <label
              for="precioInicial"
              class="w-100 d-flex align-items-center justify-content-between"
              :class="{
                'fw-bold': !isPagoUnicoSelected,
                'opacity-50': fechaVencimientoHitoActualAlreadyPayed !== null,
                pointer: fechaVencimientoHitoActualAlreadyPayed === null,
              }">
              <span class="me-4 text-primary">
                {{ $t('reserva.pagarPlazos') }}
              </span>
              <span class="d-flex justify-content-center align-items-center me-4">
                <RadioButtonIcon :is-selected="!isPagoUnicoSelected" />
              </span>
            </label>
          </div>
          <div v-else class="w-100 d-flex align-items-center justify-content-between">
            <span class="me-4 text-primary fw-bold">
              {{ $t('reserva.pagarPlazos') }}
            </span>
            <span class="d-flex justify-content-center align-items-center me-4">
              <RadioButtonIcon :is-selected="true" />
            </span>
          </div>
          <div class="card-plazos text-primary">
            <div
              v-for="(hito, index) in hitosPago"
              :key="index"
              class="pago"
              :class="{ 'opacity-50': fechaVencimientoHitoActualAlreadyPayed !== null }">
              <div v-if="index !== hitosPago.length - 1">
                <span class="pagos me-2">{{ $t('reserva.pago') }} {{ index + 1 }}</span>
                <span v-if="index === 0 && !isPagosSucesivos">
                  {{ $t('reserva.paga') }} {{ currency(hito.cantidad) }} {{ agenciaStore.getCurrency }}
                  {{ $t('general.ahora') }}.
                </span>
                <span v-else>
                  {{ $t('reserva.paga') }} {{ currency(hito.cantidad) }} {{ agenciaStore.getCurrency }}
                  {{ $t('general.antesDel') }} {{ formatDate(hito.fecha) }}.
                </span>
              </div>
              <div v-else>
                <span class="pagos me-2">{{ $t('reserva.pago') }} {{ index + 1 }}</span>
                <span>
                  {{ $t('reserva.paga') }} {{ currency(hito.cantidad) }} {{ agenciaStore.getCurrency }}
                  {{ $t('general.antesDel') }} {{ formatDate(hito.fecha) }}.
                </span>
              </div>
            </div>
          </div>
          <span v-if="fechaVencimientoHitoActualAlreadyPayed !== null" class="fechavenc-span">
            {{
              $t('reserva.pagoPlazosDeshabilitado', {
                fecha: formatDate(fechaVencimientoHitoActualAlreadyPayed),
              })
            }}
          </span>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
  import currency from 'currency.js';
  import { formatDate } from '@/helpers/dateUtiles';
  import RadioButtonIcon from '@/components/Icons/RadioButtonIcon.vue';
  import { computed, nextTick, onMounted, ref } from 'vue';
  import { TIPOS_PAGO } from '@/constants/viajeConstants';
  import { agenciaStoreModule } from '@/store_pinia/agencia';

  const props = defineProps({
    viaje: {
      type: Object, //VGViajeDTO
      required: true,
    },
    hitosPago: {
      type: Array,
      required: true,
    },
    isPagoUnicoForced: {
      type: Boolean,
      required: false,
      default: false,
    },
    isPagosSucesivos: {
      type: Boolean,
      required: false,
      default: false,
    },
    precioTotal: {
      type: Number,
      required: true,
    },
    totalPagado: {
      type: Number,
      required: false,
      default: 0,
    },
    fechaVencimientoHitoActualAlreadyPayed: {
      type: Boolean,
      required: false,
      default: false,
    },
  });

  const emits = defineEmits(['pago-unico']);

  const agenciaStore = agenciaStoreModule();

  const isPagoUnicoSelected = ref(true);

  const isPagoExternoWithHitos = computed(
    () => props.viaje?.tipoPago === TIPOS_PAGO.EXTERNO && props.hitosPago?.length > 1
  );
  const totalPagarRestante = computed(() =>
    currency(props.precioTotal).subtract(props.totalPagado)
  );

  function optionsChange(selectedValue) {
    isPagoUnicoSelected.value = selectedValue;
    emits('pago-unico', selectedValue);
  }

  onMounted(async () => {
    await nextTick();
    emits('pago-unico', isPagoUnicoSelected.value);
  });
</script>

<style scoped lang="scss">
  .card-resumen {
    border-radius: var(--bs-border-radius-xl);
    border: 1px solid rgba(var(--bs-primary-rgb), 0.25);
  }

  .card-body {
    padding: 2rem;
  }

  .title {
    font-size: 16px;
    font-weight: 500;
  }

  .text-primary {
    color: var(--bs-primary);
  }

  .pagos {
    font-size: 12px;
    font-weight: 700;
    color: var(--bs-secondary);
  }

  .fechavenc-span {
    font-size: 13px;
    color: var(--bs-primary);
  }
</style>
